import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { decompressData } from '../utils/compression';
import { getWsUrl } from '../config';

interface FlightStateInfo {
  avg_speed?: number;
  max_speed?: number;
  overall_avg_speed?: number;
  speed_count?: number;
  altitude_change?: number;
  confidence?: string;
  last_updated?: string;
  state?: string;
  flight_time?: number;
  heading?: number;
  vario?: number;
  speed?: number;
}

interface Pilot {
  id: string;
  name: string;
  lat: number;
  lng: number;
  altitude: number;
  speed: number;
  heading: number;
  vario: number;
  timestamp: number;
  color: string;
  x_mercator?: number;
  y_mercator?: number;
  // Additional metadata
  source?: string;
  lastFixTime?: string;
  lastSentPointTime?: string;
  isActive?: boolean;
  flightState?: string;
  flightStateInfo?: FlightStateInfo;
  trackHistory?: any[];
  altitudeChange?: number;
  maxSpeed?: number;
  avgSpeed?: number;
  confidence?: string;
  flightTime?: number;
  totalPoints?: number;
  firstFix?: {
    lat: number;
    lon: number;
    elevation: number;
    datetime: string;
  };
}

interface TaskWaypoint {
  seq: number;
  name: string;
  type: number;
  radius: number;
  coordinates: [number, number];
}

interface Task {
  task_name: string;
  waypoints: TaskWaypoint[];
  goal_task_type?: string;
}

interface RaceMetadata {
  race_name?: string;
  race_id?: string;
  location?: string;
  center?: { lat: number; lng: number };
  bounds?: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
}

interface WebSocketContextType {
  isConnected: boolean;
  pilots: Map<string, Pilot>;
  tasks: Task[];
  raceMetadata: RaceMetadata | null;
  lastUpdate: Date | null;
  messageCount: number;
  deltaCount: number;
  initialDataComplete: boolean;
  pilotsLoading: boolean;
  waitingForPilots: boolean;
  raceNotFound: boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  raceId: string;
  children: React.ReactNode;
}

// Admin token from config

const pilotColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#FD79A8', '#74B9FF', '#A29BFE'];

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ raceId, children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [pilots, setPilots] = useState<Map<string, Pilot>>(new Map());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [raceMetadata, setRaceMetadata] = useState<RaceMetadata | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [messageCount, setMessageCount] = useState(0);
  const [deltaCount, setDeltaCount] = useState(0);
  const [initialDataComplete, setInitialDataComplete] = useState(false);
  const [pilotsLoading, setPilotsLoading] = useState(true);
  const [waitingForPilots, setWaitingForPilots] = useState(true);
  const [raceNotFound, setRaceNotFound] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pilotTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const colorMapRef = useRef<Map<string, string>>(new Map());
  const pilotsRef = useRef<Map<string, Pilot>>(new Map());
  const isInitializedRef = useRef<boolean>(false);
  const bufferedDeltaUpdatesRef = useRef<any[]>([]);
  const raceNotFoundRef = useRef<boolean>(false);

  const getOrAssignColor = useCallback((pilotId: string): string => {
    if (!colorMapRef.current.has(pilotId)) {
      const usedColors = Array.from(colorMapRef.current.values());
      const availableColors = pilotColors.filter(c => !usedColors.includes(c));
      const color = availableColors.length > 0
        ? availableColors[0]
        : pilotColors[colorMapRef.current.size % pilotColors.length];
      colorMapRef.current.set(pilotId, color);
    }
    return colorMapRef.current.get(pilotId)!;
  }, []);


  // Forward declare procesBufferedDeltaUpdates
  const procesBufferedDeltaUpdatesRef = useRef<() => Promise<void>>();

  const connectWebSocket = useCallback(() => {
    // Prevent double connection in StrictMode
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket already exists, skipping connection. ReadyState:', wsRef.current?.readyState);
      return;
    }
    
    // Close any existing connection first
    if (wsRef.current) {
      console.log('Closing existing WebSocket connection before creating new one');
      wsRef.current.close();
      wsRef.current = null;
    }

    // Build WebSocket URL for TILE-BASED endpoint with race metadata
    const clientId = `webapp_${Date.now()}`;
    // Use the tile-based endpoint that includes race_metadata
    const wsUrl = getWsUrl(`/tracking/ws/tiles/${raceId}?client_id=${clientId}`);

    // Connecting to WebSocket

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      setPilotsLoading(true);
      setWaitingForPilots(true);
      // Reset race not found state for new connection
      setRaceNotFound(false);
      raceNotFoundRef.current = false;

      // Request initial data immediately without delay
      // Reset initialization state when requesting new data
      isInitializedRef.current = false;
      bufferedDeltaUpdatesRef.current = [];

      // Set a short timeout as fallback - most data arrives within 100ms
      initTimeoutRef.current = setTimeout(() => {
        if (!isInitializedRef.current && !raceNotFoundRef.current) {
          isInitializedRef.current = true;
          procesBufferedDeltaUpdates();
        }
      }, 300);

      // After 3 seconds, if no pilots arrived, show "No pilots live yet"
      pilotTimeoutRef.current = setTimeout(() => {
        if (pilotsRef.current.size === 0 && !raceNotFoundRef.current) {
          setPilotsLoading(false);
          setWaitingForPilots(false);
        }
      }, 3000);

      // Determine if we should send bbox based on existing pilots
      let requestMessage: any = {
        type: 'request_initial_data',
        include_pilots: true,  // Request pilot data
        include_tasks: true    // Request task data
      };

      // If we had pilots before, include bbox for their area
      if (pilotsRef.current.size > 0) {
        const bounds = Array.from(pilotsRef.current.values());
        const minLat = Math.min(...bounds.map(p => p.lat));
        const maxLat = Math.max(...bounds.map(p => p.lat));
        const minLng = Math.min(...bounds.map(p => p.lng));
        const maxLng = Math.max(...bounds.map(p => p.lng));

        requestMessage.bbox = [minLng, minLat, maxLng, maxLat];
      }

      // Send only ONE request for initial data
      ws.send(JSON.stringify(requestMessage));

      // Start ping interval
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setMessageCount(prev => prev + 1);

        switch (data.type) {
          case 'error':
            // If race not found, handle it gracefully
            if (data.message === 'Race not found' || data.error === 'Race not found') {
              // Only log once
              if (!raceNotFoundRef.current) {
                console.log('ℹ️ Race not found:', raceId);

                // Clear all timers immediately
                if (initTimeoutRef.current) {
                  clearTimeout(initTimeoutRef.current);
                  initTimeoutRef.current = null;
                }
                if (pilotTimeoutRef.current) {
                  clearTimeout(pilotTimeoutRef.current);
                  pilotTimeoutRef.current = null;
                }
                if (reconnectTimeoutRef.current) {
                  clearTimeout(reconnectTimeoutRef.current);
                  reconnectTimeoutRef.current = null;
                }
                if (pingIntervalRef.current) {
                  clearInterval(pingIntervalRef.current);
                  pingIntervalRef.current = null;
                }

                // Set states
                setPilotsLoading(false);
                setWaitingForPilots(false);
                setRaceNotFound(true);
                raceNotFoundRef.current = true;
                isInitializedRef.current = true;
              }

              // Close the connection without logging
              if (ws.readyState === WebSocket.OPEN) {
                ws.close(1000, 'Race not found');
              }
            } else {
              console.error('⚠️ WebSocket error:', data.message || data.error);
            }
            break;
          case 'delta_update':
            handleDeltaUpdate(data);
            break;
          case 'tile_data':
            handleTileData(data);
            break;
          case 'race_metadata':
            // Handle the new race_metadata message from tile-based endpoint
            const metadata: RaceMetadata = {
              race_name: data.race_name,
              race_id: data.race_id,
              location: data.location,
              center: data.center,
              bounds: data.bounds
            };

            setRaceMetadata(metadata);

            // Race metadata received

            // Log full data to debug tasks issue
            // Full race metadata processed

            if (data.tasks && Array.isArray(data.tasks)) {
              // Processing raw tasks
              // Transform tasks to match our interface
              const transformedTasks: Task[] = data.tasks.map((task: any) => ({
                task_name: task.properties?.task_name || task.task_name || task.name || '',
                waypoints: (task.waypoints || []).map((wp: any, index: number) => {
                  // Get coordinates from geometry array using the index
                  const coords = task.geometry?.coordinates?.[index] || [0, 0];
                  return {
                    seq: wp.seq || index + 1,
                    name: wp.name || '',
                    type: wp.type !== undefined ? wp.type : 1,
                    radius: wp.radius || 400,
                    coordinates: coords
                  };
                }),
                goal_task_type: task.properties?.goal_task_type || task.goal_task_type
              }));
              setTasks(transformedTasks);
              // Tasks loaded from metadata
            } else {
              console.log('⚠️ No tasks in race_metadata message');
            }

            // Process pilots if included in race_metadata
            if (data.pilots && Array.isArray(data.pilots)) {
              console.log('📋 Processing pilots from race_metadata:', data.pilots.length);
              const newPilots = new Map<string, Pilot>();

              data.pilots.forEach((pilot: any) => {
                if (pilot.id && pilot.lat && pilot.lng) {
                  newPilots.set(pilot.id, {
                    id: pilot.id,
                    name: pilot.name || `Pilot ${pilot.id}`,
                    lat: pilot.lat,
                    lng: pilot.lng,
                    altitude: pilot.altitude || pilot.alt || 0,
                    heading: pilot.heading || 0,
                    speed: pilot.speed || 0,
                    vario: pilot.vario || 0,
                    timestamp: pilot.timestamp || Date.now(),
                    color: pilot.color || '#FF0000'
                  });
                }
              });

              setPilots(newPilots);
              console.log('✅ Initial pilots loaded from race_metadata:', newPilots.size);
            }

            // Mark as initialized after race_metadata is received
            // This is the response to request_initial_data in tile-based protocol
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              // Ready for delta updates

              // Process any buffered delta updates
              procesBufferedDeltaUpdates();
            }

            // Mark initial data as complete since race_metadata is the response to request_initial_data
            setInitialDataComplete(true);
            break;

          case 'race_config':
            // Legacy race_config handler (keeping for backward compatibility)
            // Legacy race config received
            if (data.tasks && Array.isArray(data.tasks)) {
              // Transform tasks to match our interface
              const transformedTasks: Task[] = data.tasks.map((task: any) => ({
                task_name: task.properties?.task_name || task.task_name || '',
                waypoints: (task.waypoints || []).map((wp: any, index: number) => {
                  // Get coordinates from geometry array using the index
                  const coords = task.geometry?.coordinates?.[index] || [0, 0];
                  return {
                    seq: wp.seq || index + 1,
                    name: wp.name || '',
                    type: wp.type !== undefined ? wp.type : 1,
                    radius: wp.radius || 400,
                    coordinates: coords
                  };
                }),
                goal_task_type: task.properties?.goal_task_type || task.goal_task_type
              }));
              setTasks(transformedTasks);
              // Tasks loaded
            }
            break;
          case 'initial_data':
            handleInitialData(data);
            break;
          case 'initial_data_complete':
            console.log('Initial data complete, tiles sent:', data.tiles_sent);
            // Also mark as initialized when we get this message
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              console.log('✅ Initial data complete - ready for delta updates');
              procesBufferedDeltaUpdates();
            }
            setInitialDataComplete(true);
            break;
          case 'heartbeat':
            // Heartbeat received
            break;
          case 'connection_status':
            // Connection status received
            break;
          case 'viewer_count':
            console.log('Viewer count:', data.count);
            break;
          case 'pong':
            // Ping response, ignore
            break;
          case 'viewport_updated':
            // Acknowledgment that viewport was updated, no action needed
            break;
          default:
            if (data.type !== 'pong') {
              console.log('Unknown message type:', data.type);
            }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      // Only log actual errors, not race not found
      if (!raceNotFoundRef.current) {
        console.error('WebSocket error:', error);
      }
    };

    ws.onclose = (event) => {
      // Reset initialization state on disconnect
      isInitializedRef.current = false;
      bufferedDeltaUpdatesRef.current = [];
      // Only log disconnect if not race not found
      if (!raceNotFoundRef.current) {
        console.log('WebSocket disconnected', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
      }
      setIsConnected(false);
      wsRef.current = null;

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      // Only reconnect if not a race not found error
      if (event.code !== 1008 || event.reason !== 'Race not found') {
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      } else {
        // Race not found - already handled in error message
        if (!raceNotFoundRef.current) {
          setPilotsLoading(false);
          setWaitingForPilots(false);
          setRaceNotFound(true);
          raceNotFoundRef.current = true;
        }
      }
    };
  }, [raceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeltaUpdate = useCallback(async (data: any) => {
    // Process delta updates immediately if we have pilots, otherwise buffer briefly
    if (!isInitializedRef.current && pilotsRef.current.size === 0) {
      // Buffering first delta update
      bufferedDeltaUpdatesRef.current.push(data);
      // Process after a very short delay if still no race_metadata
      setTimeout(() => {
        if (!isInitializedRef.current) {
          isInitializedRef.current = true;
          procesBufferedDeltaUpdates();
        }
      }, 100);
      return;
    }

    setDeltaCount(prev => prev + 1);
    setLastUpdate(new Date());

    try {
      let decompressedData: any = null;

      // Decompress if needed
      if (data.data && data.compression === 'gzip') {
        decompressedData = await decompressData(data.data);
      } else if (data.updates) {
        decompressedData = { updates: data.updates };
      }

      if (decompressedData && decompressedData.updates && Array.isArray(decompressedData.updates)) {
        // Log detailed delta update data
        // Delta update received
        
        // Check if this is the first pilots arriving
        const hadNoPilots = pilotsRef.current.size === 0;

        // Update pilot positions with new points
        setPilots(currentPilots => {
          const newPilots = new Map(currentPilots);

          decompressedData.updates.forEach((update: any) => {
            // Pilot update received

            // Processing pilot update

            let lat = update.lat;
            let lng = update.lon || update.lng || update.longitude;

            // Skip invalid coordinates
            if (typeof lat !== 'number' || isNaN(lat) || !isFinite(lat) || lat < -90 || lat > 90) return;
            if (typeof lng !== 'number' || isNaN(lng) || !isFinite(lng) || lng < -180 || lng > 180) return;

            const existingPilot = currentPilots.get(update.pilot_id);

            // Process historical track_points
            let trackHistory: any[] = existingPilot?.trackHistory || [];

            // Add new track points if provided
            if (update.track_points && Array.isArray(update.track_points)) {
              // Processing historical track points - appending to existing history
              const newPoints = update.track_points.map((point: any) => ({
                lng: point.lon || point.lng || point.longitude,
                lat: point.lat,
                altitude: point.elevation || point.altitude || 0,
                timestamp: point.timestamp || Date.now(),
                speed: point.speed || 0,
                vario: point.vario || 0,
                heading: point.heading || 0
              }));

              // Merge new points with existing history, avoiding duplicates by timestamp
              const existingTimestamps = new Set(trackHistory.map((p: any) => p.timestamp));
              const uniqueNewPoints = newPoints.filter((p: any) => !existingTimestamps.has(p.timestamp));

              // Append unique new points and sort by timestamp
              trackHistory = [...trackHistory, ...uniqueNewPoints].sort((a, b) => a.timestamp - b.timestamp);

              // Limit history to prevent memory issues (keep last 1000 points)
              if (trackHistory.length > 1000) {
                trackHistory = trackHistory.slice(-1000);
              }
            }

            // Skip if position hasn't changed (deduplication)
            if (existingPilot && existingPilot.lat === lat && existingPilot.lng === lng) {
              // Position unchanged, but still update trackHistory if new points were added
              if (update.track_points && Array.isArray(update.track_points) && update.track_points.length > 0) {
                // Even if pilot position unchanged, we got new track points, so update the pilot
                newPilots.set(update.pilot_id, {
                  ...existingPilot,
                  trackHistory: trackHistory,
                  timestamp: update.timestamp ? new Date(update.timestamp).getTime() : Date.now()
                });
              }
              return;
            }

            // Add current position to track history if it's new
            const currentTimestamp = update.timestamp || Date.now();
            if (!trackHistory.some(point =>
              point.lng === lng &&
              point.lat === lat &&
              point.timestamp === currentTimestamp
            )) {
              trackHistory.push({
                lng: lng,
                lat: lat,
                altitude: update.elevation || update.altitude || 0,
                timestamp: currentTimestamp,
                speed: update.speed || 0,
                vario: update.vario || 0,
                heading: update.heading || 0
              });

              // Sort by timestamp to maintain chronological order
              trackHistory.sort((a, b) => a.timestamp - b.timestamp);

              // Limit history
              if (trackHistory.length > 1000) {
                trackHistory = trackHistory.slice(-1000);
              }
            }

            // Build updated pilot with new track point added to history
            const pilotData: Pilot = {
              id: update.pilot_id,
              name: update.pilot_name || existingPilot?.name || 'Unknown',
              lat: lat,
              lng: lng,
              altitude: update.elevation || update.altitude || 0,
              speed: update.speed || 0,
              heading: update.heading || 0,
              vario: update.vario || 0,
              timestamp: update.timestamp ? new Date(update.timestamp).getTime() : Date.now(),
              color: existingPilot?.color || getOrAssignColor(update.pilot_id),
              x_mercator: update.x_mercator,
              y_mercator: update.y_mercator,
              // Maintain track history from track_points
              trackHistory: trackHistory,
              // Update with new metadata from delta
              source: update.source || existingPilot?.source,
              lastFixTime: update.last_fix?.datetime || existingPilot?.lastFixTime,
              lastSentPointTime: update.timestamp || existingPilot?.lastSentPointTime,
              isActive: true,
              flightState: update.flight_state || existingPilot?.flightState,
              flightStateInfo: update.flight_state_info || existingPilot?.flightStateInfo,
              altitudeChange: update.flight_state_info?.altitude_change ?? existingPilot?.altitudeChange,
              maxSpeed: update.flight_state_info?.max_speed || existingPilot?.maxSpeed,
              avgSpeed: update.flight_state_info?.overall_avg_speed || existingPilot?.avgSpeed,
              confidence: update.flight_state_info?.confidence || existingPilot?.confidence,
              // Use flight_time from flight_state_info if available, otherwise use update.flight_time
              flightTime: update.flight_state_info?.flight_time || update.flight_time || existingPilot?.flightTime,
              totalPoints: update.total_points,
              firstFix: update.first_fix || existingPilot?.firstFix
            };
            
            // Position updated
            newPilots.set(update.pilot_id, pilotData);
          });
          
          return newPilots;
        });

        // If this was the first pilots arriving, update loading states
        if (hadNoPilots && decompressedData.updates.length > 0) {
          setPilotsLoading(false);
          setWaitingForPilots(false);
          // Auto-focusing on first pilots

          // Trigger auto-focus event - the map will listen to this
          window.dispatchEvent(new CustomEvent('pilotsArrived', {
            detail: {
              firstPilotLoad: true,
              pilotCount: decompressedData.updates.length
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error processing delta update:', error);
    }
  }, [getOrAssignColor]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleTileData = (data: any) => {
    // Tile data would need MVT decoding
    // For now, we rely on delta updates
    if (data.tile) {
      // Tile data received
    }
  };

  const procesBufferedDeltaUpdates = useCallback(async () => {
    if (bufferedDeltaUpdatesRef.current.length > 0) {
      // Processing buffered updates
      for (const bufferedUpdate of bufferedDeltaUpdatesRef.current) {
        await handleDeltaUpdate(bufferedUpdate);
      }
      bufferedDeltaUpdatesRef.current = [];
    }
  }, [handleDeltaUpdate]);

  // Set the ref for use in connectWebSocket
  procesBufferedDeltaUpdatesRef.current = procesBufferedDeltaUpdates;

  const handleInitialData = useCallback((data: any) => {
    // Initial data received

    // Handle tasks if present
    if (data.tasks && Array.isArray(data.tasks)) {
      // Tasks received
      const transformedTasks: Task[] = data.tasks.map((task: any) => ({
        task_name: task.properties?.task_name || task.task_name || '',
        waypoints: (task.waypoints || []).map((wp: any, index: number) => {
          // Get coordinates from geometry array using the index
          const coords = task.geometry?.coordinates?.[index] || (wp.lon !== undefined && wp.lat !== undefined ? [wp.lon, wp.lat] : [0, 0]);
          return {
            seq: wp.seq || index + 1,
            name: wp.name || '',
            type: wp.type !== undefined ? wp.type : 1,
            radius: wp.radius || 400,
            coordinates: coords
          };
        }),
        goal_task_type: task.properties?.goal_task_type || task.goal_task_type
      }));
      setTasks(transformedTasks);
      // Tasks loaded
    }
    
    // Handle flights array (from production service)
    if (data.flights && Array.isArray(data.flights)) {
      const newPilots = new Map<string, Pilot>();
      
      data.flights.forEach((flight: any) => {
        // Use lastFix for current position
        const lastFix = flight.lastFix || {};
        const flightStateInfo = flight.flight_state_info || {};
        
        // Log the raw data to debug coordinate issues
        // Process flight data
        
        // Validate coordinates before creating pilot data
        const lat = lastFix.lat || lastFix.latitude || flight.lat || 0;
        const lng = lastFix.lon || lastFix.lng || lastFix.longitude || flight.lon || flight.lng || 0;
        
        if (typeof lat !== 'number' || isNaN(lat) || !isFinite(lat) || lat < -90 || lat > 90) {
          console.warn('Invalid latitude in initial data for pilot', flight.pilot_id, ':', lat);
          return; // Skip this flight
        }
        
        if (typeof lng !== 'number' || isNaN(lng) || !isFinite(lng) || lng < -180 || lng > 180) {
          console.warn('Invalid longitude in initial data for pilot', flight.pilot_id, ':', lng);
          return; // Skip this flight
        }

        const pilotData: Pilot = {
          id: flight.pilot_id || flight.uuid,
          name: flight.pilot_name || 'Unknown',
          lat: lat,
          lng: lng,
          altitude: lastFix.elevation || lastFix.altitude || 0,
          speed: flightStateInfo.avg_speed || flightStateInfo.speed || 0,
          heading: flightStateInfo.heading || 0,
          vario: flightStateInfo.vario || 0,
          timestamp: lastFix.datetime ? new Date(lastFix.datetime).getTime() : Date.now(),
          color: getOrAssignColor(flight.pilot_id || flight.uuid),
          // Store all additional metadata
          source: flight.source,
          lastFixTime: flight.lastFixTime,
          lastSentPointTime: flight.lastSentPointTime,
          isActive: flight.isActive,
          flightState: flight.flight_state,
          flightStateInfo: flightStateInfo,
          trackHistory: flight.trackHistory || [{ 
            lat, 
            lng, 
            altitude: lastFix.elevation || lastFix.altitude || 0,
            timestamp: lastFix.datetime ? new Date(lastFix.datetime).getTime() : Date.now(),
            speed: flightStateInfo.avg_speed || flightStateInfo.speed || 0,
            heading: flightStateInfo.heading || 0,
            vario: flightStateInfo.vario || 0
          }],
          altitudeChange: flightStateInfo.altitude_change,
          maxSpeed: flightStateInfo.max_speed,
          avgSpeed: flightStateInfo.avg_speed || flightStateInfo.overall_avg_speed,
          confidence: flightStateInfo.confidence,
          // Ensure flight time is set from flightStateInfo
          flightTime: flightStateInfo.flight_time || flight.flight_time || 0
        };
        // Initial pilot loaded
        newPilots.set(pilotData.id, pilotData);
      });
      
      // Merge with existing pilots, keeping newer data
      setPilots(currentPilots => {
        const mergedPilots = new Map(currentPilots);
        
        newPilots.forEach((newPilot, pilotId) => {
          const existingPilot = mergedPilots.get(pilotId);
          
          // Only update if this data is newer or pilot doesn't exist
          if (!existingPilot || newPilot.timestamp > existingPilot.timestamp) {
            // Using newer data
            mergedPilots.set(pilotId, newPilot);
          } else {
            // Keeping existing newer data
          }
        });
        
        // Set pilot state
        return mergedPilots;
      });

      // Mark as initialized after pilots are loaded
      isInitializedRef.current = true;
      // Ready for delta updates

      // Process any buffered delta updates
      procesBufferedDeltaUpdates();
      setInitialDataComplete(true);
    }
    // Legacy support for pilots array
    else if (data.pilots && Array.isArray(data.pilots)) {
      const newPilots = new Map<string, Pilot>();
      
      data.pilots.forEach((pilot: any) => {
        const pilotData = {
          id: pilot.id,
          name: pilot.name,
          lat: pilot.lat,
          lng: pilot.lng || pilot.lon,
          altitude: pilot.altitude || 0,
          speed: pilot.speed || 0,
          heading: pilot.heading || 0,
          vario: pilot.vario || 0,
          timestamp: pilot.timestamp || Date.now(),
          color: getOrAssignColor(pilot.id)
        };
        // Add pilot
        newPilots.set(pilot.id, pilotData);
      });
      
      console.log(`✅ ${newPilots.size} pilots loaded`);
      setPilots(newPilots);

      // Mark as initialized after pilots are loaded (legacy path)
      isInitializedRef.current = true;
      // Ready for delta updates

      // Process any buffered delta updates
      procesBufferedDeltaUpdates();
      setInitialDataComplete(true);
    } else {
      // No pilot data in initial message, mark as initialized anyway
      isInitializedRef.current = true;
      // No initial pilot data
      procesBufferedDeltaUpdates();
      setInitialDataComplete(true);
    }
  }, [getOrAssignColor, procesBufferedDeltaUpdates]); // getOrAssignColor is needed for pilot color assignment

  // Keep pilotsRef in sync with pilots state
  useEffect(() => {
    pilotsRef.current = pilots;
  }, [pilots]);

  useEffect(() => {
    let isCancelled = false;
    
    // Small delay to prevent StrictMode double-mount issues
    const timer = setTimeout(() => {
      if (!isCancelled) {
        connectWebSocket();
      }
    }, 100);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
      
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      // Clear all timers on unmount
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      if (pilotTimeoutRef.current) {
        clearTimeout(pilotTimeoutRef.current);
      }
    };
  }, [raceId, connectWebSocket]);

  return (
    <WebSocketContext.Provider value={{
      isConnected,
      pilots,
      tasks,
      raceMetadata,
      lastUpdate,
      messageCount,
      deltaCount,
      initialDataComplete,
      pilotsLoading,
      waitingForPilots,
      raceNotFound
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};